import { ReactNode, useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { cn } from '@hqo/react-hooks';
import { Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@hqo/ui-atoms';
import { useMobileScreen } from 'app/shared/hooks/use-mobile-screen';

/** Bold the resource name + when in the confirm copy so the where/when reads at a glance. */
const renderBold = (chunks: ReactNode): JSX.Element => <strong className="font-semibold">{chunks}</strong>;

interface BookingCancelModalProps {
  isOpen: boolean;
  resourceName: string;
  /** Formatted "Friday, June 5, 2026 · 1:45 PM – 2:45 PM". */
  when: string;
  cancelable: boolean;
  isCancelling: boolean;
  isError: boolean;
  onConfirm: () => void;
  /** Keep the booking / dismiss (overlay click, Esc, or the "Keep booking" button). */
  onClose: () => void;
}

/**
 * Cancel-confirmation modal over the booking receipt (the miniapp-content confirm-dialog pattern):
 * confirm the cancellation or keep the booking. On success the page dismisses this and shows the
 * cancelled state in-place.
 */
export const BookingCancelModal = ({
  isOpen,
  resourceName,
  when,
  cancelable,
  isCancelling,
  isError,
  onConfirm,
  onClose,
}: BookingCancelModalProps): JSX.Element => {
  const intl = useIntl();
  const { isMobileScreen } = useMobileScreen();
  // Land initial focus on the SAFE action, not the destructive confirm — a stray Enter on open must
  // not cancel the booking (Radix otherwise auto-focuses the first focusable, the destructive button).
  // The ui-atoms Button doesn't forward refs, so focus the "Keep" button by its test id.
  const focusSafeAction = useCallback((event: Event) => {
    event.preventDefault();
    const root = event.currentTarget as HTMLElement | null;
    root?.querySelector<HTMLButtonElement>('[data-testid="booking-cancel-keep"]')?.focus();
  }, []);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className={cn(
          'border-none p-6',
          // Mobile: bottom sheet that slides up (the miniapp-content pattern). Desktop: centered dialog.
          isMobileScreen
            ? [
              'bottom-0 left-0 top-auto w-full max-w-full translate-x-0 translate-y-0 rounded-b-none rounded-t-2xl sm:max-w-full',
              'data-[state=open]:fade-in-100 data-[state=closed]:fade-out-100',
              'data-[state=open]:zoom-in-100 data-[state=closed]:zoom-out-100',
              'data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom duration-500',
            ]
            : 'rounded-2xl sm:max-w-[440px]',
        )}
        data-testid="booking-cancel-modal"
        onOpenAutoFocus={focusSafeAction}
      >
        <DialogHeader className="text-left">
          <DialogTitle className="text-xl font-semibold text-foreground">
            {intl.formatMessage({ id: 'booking.detail.cancel.title' })}
          </DialogTitle>
        </DialogHeader>
        <DialogDescription className="text-[15px] leading-6 text-system-grey-dark">
          <FormattedMessage
            id="booking.detail.cancel.body"
            values={{ resource: resourceName, when, b: renderBold }}
          />
        </DialogDescription>

        {isError && (
          <p className="text-sm text-system-pink" role="alert" data-testid="booking-cancel-error">
            {intl.formatMessage({ id: 'booking.detail.cancel.error' })}
          </p>
        )}

        <DialogFooter className="mt-1 flex flex-col gap-2.5 sm:flex-col sm:space-x-0">
          <Button
            type="button"
            variant="destructive"
            onClick={onConfirm}
            disabled={!cancelable || isCancelling}
            className="h-12 w-full text-sm font-semibold"
            data-testid="booking-cancel-confirm"
          >
            {intl.formatMessage({ id: isCancelling ? 'booking.detail.cancel.cancelling' : 'booking.detail.cancel.confirm' })}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={isCancelling}
            className="h-12 w-full text-sm font-semibold hover:bg-system-grey-light hover:text-foreground"
            data-testid="booking-cancel-keep"
          >
            {intl.formatMessage({ id: 'booking.detail.cancel.keep' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
